import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

export default function PaymentReturn({ cancelled }) {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (cancelled) return;
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("success");
          refreshUser();
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch { /* keep polling */ }
      polls += 1;
      if (polls >= MAX_POLLS) { setState("timeout"); return; }
      timer = setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
  }, [cancelled, params, refreshUser]);

  const label = {
    credits: "Credits have been added to your account.",
    prize_fund: "The contest prize pool has been funded.",
    estates: "Domain Estates have been minted into inventory.",
    booklet_activation: "Your booklet is activated and $15.00 in race credits have been added.",
  };

  return (
    <div className="max-w-md mx-auto mt-16 text-center animate-slide-up" data-testid="payment-return">
      {state === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="animate-spin mx-auto text-race-red" size={48} />
          <h1 className="font-heading text-3xl tracking-tight mt-4">CONFIRMING PAYMENT…</h1>
          <p className="text-race-sub text-sm mt-2">Hang tight while Stripe confirms your purchase.</p>
        </div>
      )}
      {state === "success" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="mx-auto text-green-400" size={56} />
          <h1 className="font-heading text-4xl tracking-tight mt-4">PAYMENT CONFIRMED</h1>
          <p className="text-race-sub text-sm mt-2">{label[info?.purpose] || "Your purchase is complete."}</p>
          <Link to="/dashboard" data-testid="payment-back" className="inline-block mt-6 bg-race-red hover:bg-race-redhover text-race-bg font-mono text-xs uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">Back to Dashboard</Link>
        </div>
      )}
      {(state === "cancelled" || state === "error" || state === "timeout") && (
        <div data-testid="payment-failed">
          <XCircle className="mx-auto text-race-red" size={56} />
          <h1 className="font-heading text-4xl tracking-tight mt-4">
            {state === "cancelled" ? "PAYMENT CANCELLED" : state === "timeout" ? "STILL PROCESSING" : "PAYMENT ISSUE"}
          </h1>
          <p className="text-race-sub text-sm mt-2">
            {state === "cancelled" ? "No charge was made. You can try again anytime."
              : state === "timeout" ? "This is taking longer than expected. Your account will update automatically once confirmed."
              : "We couldn't confirm this payment. If you were charged, it will reconcile shortly."}
          </p>
          <Link to="/dashboard" data-testid="payment-back" className="inline-block mt-6 border border-race-line hover:border-race-red text-race-txt font-mono text-xs uppercase tracking-wide px-6 py-3 rounded-sm transition-colors">Back to Dashboard</Link>
        </div>
      )}
    </div>
  );
}
